import {
  Grid,
  TextField,
  Link,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import { green } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import InputMask from "react-input-mask";
import React from "react";
import { storage } from "../../services/firebase";
import api from "../../services/api";
import { useSnackbar } from 'notistack';

const Input = styled("input")({
  display: "none",
});

export default function AddressForm() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [hash, setHash] = React.useState(
    sessionStorage.getItem("comprovante_hash")
  );
  const [idFile, setIdFile] = React.useState(
    sessionStorage.getItem("comprovante_id")
  );
  const [numcep, setCep] = React.useState(sessionStorage.getItem("cep") || '');
  const [numero, setNumero] = React.useState(sessionStorage.getItem("numero") || '');
  const [file_temp, setFile] = React.useState({
    name: sessionStorage.getItem("file_name"),
    size: "",
    url: "",
  });
  const [errorCep, setErrorCep] = React.useState(null);
  const [loadingCep, setLoadingCep] = React.useState(false);
  const [UseEndereco, setUseEndereco] = React.useState({
    rua: sessionStorage.getItem("rua") || '',
    bairro: sessionStorage.getItem("bairro") || '',
    municipio: sessionStorage.getItem("municipio") || '',
    estado: sessionStorage.getItem("estado") || '',
  });
  
  // Refs para controlar debounce e cancelar requisições
  const debounceTimerRef = React.useRef(null);
  const abortControllerRef = React.useRef(null);
  const lastCepConsultedRef = React.useRef(null);

  const handleNumero = (event) => {
    const {
      target: { value },
    } = event;
    setNumero(value);
    if (value.length > 0) sessionStorage.setItem("numero", value);
    else {
      sessionStorage.removeItem("numero");
    }
  };

  const handleAddressField = (event, field) => {
    const {
      target: { value },
    } = event;

    const currentAddress = {...UseEndereco}
    currentAddress[field] = value;
    setUseEndereco(currentAddress);

    if (value.length > 0) sessionStorage.setItem(field, value);
    else {
      sessionStorage.removeItem(field);
    }
  };

  const consultarCep = React.useCallback((cepLimpo) => {
    // Evita consultar o mesmo CEP novamente (já consultado com sucesso)
    if (lastCepConsultedRef.current === cepLimpo) {
      return;
    }

    // Se já existe uma requisição em andamento para o mesmo CEP, não faz nada
    // Isso evita cancelar a requisição que está sendo feita pelo debounce
    const cepEmAndamento = abortControllerRef.current?.cepEmAndamento;
    if (cepEmAndamento === cepLimpo && abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      // Já existe uma requisição em andamento para este CEP, não faz nada
      return;
    }

    // Cancela requisição anterior se existir e for para um CEP diferente
    if (abortControllerRef.current && cepEmAndamento !== cepLimpo) {
      abortControllerRef.current.abort();
    }

    // Cria novo AbortController
    abortControllerRef.current = new AbortController();
    abortControllerRef.current.cepEmAndamento = cepLimpo; // Marca qual CEP está sendo consultado
    const signal = abortControllerRef.current.signal;

    setLoadingCep(true);
    setErrorCep(null);

    api
      .get(`cep/${cepLimpo}`, { signal })
      .then((response) => {
        // Ignora se a requisição foi cancelada
        if (signal.aborted) {
          setLoadingCep(false);
          return;
        }

        const data = response.data;
        const rua = data.street || '';
        const bairro = data.neighborhood || '';
        const municipio = data.city || '';
        const estado = data.state || '';
        const cep = data.cep || '';
        
        setUseEndereco({
          rua,
          bairro,
          municipio,
          cep,
          estado,
        });
        sessionStorage.setItem("cep", cep);
        sessionStorage.setItem("estado", estado);
        sessionStorage.setItem("municipio", municipio);
        sessionStorage.setItem("bairro", bairro);
        sessionStorage.setItem("rua", rua);
        
        lastCepConsultedRef.current = cepLimpo;
        setLoadingCep(false);
      })
      .catch((error) => {
        // Ignora se a requisição foi cancelada (não mostra erro)
        if (signal.aborted || error.name === 'AbortError' || error.code === 'ERR_CANCELED' || 
            (error.message && error.message.includes('canceled'))) {
          setLoadingCep(false);
          return;
        }

        setLoadingCep(false);
        
        // Tratamento de erros específicos
        if (error.response) {
          const status = error.response.status;
          if (status === 404) {
            setErrorCep({ numcep: "CEP não encontrado." });
          } else if (status === 429) {
            setErrorCep({ numcep: "Muitas requisições. Aguarde um momento." });
          } else if (status === 502) {
            setErrorCep({ numcep: "Falha ao consultar CEP. Tente novamente." });
          } else {
            setErrorCep({ numcep: "Não conseguimos resgatar o seu CEP." });
          }
        } else {
          // Erro de rede - não mostra erro se foi cancelado
          setErrorCep({ numcep: "Erro ao consultar CEP. Verifique sua conexão." });
        }

        // Limpa campos em caso de erro (apenas se não foi cancelado)
        if (!signal.aborted) {
          setUseEndereco({
            rua: "",
            bairro: "",
            municipio: "",
            cep: "",
            estado: "",
          });
          sessionStorage.removeItem("cep");
          sessionStorage.removeItem("estado");
          sessionStorage.removeItem("municipio");
          sessionStorage.removeItem("bairro");
          sessionStorage.removeItem("rua");
        }
      });
  }, []);

  const handleCep = (event) => {
    const {
      target: { value },
    } = event;
    setCep(value);
    setErrorCep(null);

    // Remove formatação para validar
    const cepLimpo = value.replace(/\D/g, '');

    // Limpa timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Valida se tem 8 dígitos
    if (cepLimpo.length === 8) {
      // Debounce de 450ms antes de consultar
      debounceTimerRef.current = setTimeout(() => {
        consultarCep(cepLimpo);
      }, 450);
    } else if (cepLimpo.length > 0) {
      // CEP incompleto - não mostra erro ainda, apenas limpa se tinha dados
      if (cepLimpo.length < 8) {
        // Limpa campos se o CEP foi alterado para um valor incompleto
        setUseEndereco({
          rua: "",
          bairro: "",
          municipio: "",
          cep: "",
          estado: "",
        });
        lastCepConsultedRef.current = null;
      }
    } else {
      // Campo vazio - limpa tudo
      setErrorCep({ numcep: "CEP inválido." });
      sessionStorage.removeItem("cep");
      sessionStorage.removeItem("estado");
      sessionStorage.removeItem("municipio");
      sessionStorage.removeItem("bairro");
      sessionStorage.removeItem("rua");
      lastCepConsultedRef.current = null;
    }
  };

  const handleCepBlur = (event) => {
    const {
      target: { value },
    } = event;
    const cepLimpo = value.replace(/\D/g, '');

    // Limpa timer de debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Se tem 8 dígitos e ainda não consultou com sucesso, consulta imediatamente
    // Mas só se não houver uma requisição já em andamento para este CEP
    if (cepLimpo.length === 8) {
      const cepEmAndamento = abortControllerRef.current?.cepEmAndamento;
      // Se já consultou com sucesso ou já está consultando, não faz nada
      if (lastCepConsultedRef.current !== cepLimpo && cepEmAndamento !== cepLimpo) {
        consultarCep(cepLimpo);
      }
    } else if (cepLimpo.length !== 8 && cepLimpo.length > 0) {
      setErrorCep({ numcep: "CEP inválido. Deve conter 8 dígitos." });
    }
  };

  // Cleanup ao desmontar componente
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleFile = async (info) => {
    const file = info.target.files[0];
    const isFile = file.type.indexOf("application/pdf") === 0;
    const isLt5M = file.size / 1024 / 1024 < 5;
    setLoading(true);
    setSuccess(true);
    // debugger;
    if (!isFile) {
      enqueueSnackbar("Arquivo precisa ser PDF!", { variant: "warning" });
      setLoading(false);
      setSuccess(false);
      sessionStorage.removeItem("comprovante_id");
    } else if (!isLt5M) {
      enqueueSnackbar("Arquivo deve ter tamanho máximo de 5MB!", { variant: "warning" });
      setLoading(false);
      setSuccess(false);
      sessionStorage.removeItem("comprovante_id");
    } else {
      // Gera um hash aleatório compatível com browser
      const newHash = Math.random().toString(36).substring(2, 10);
      const storageRef = await storage.ref();
      // debugger;
      var File = storageRef.child(`/teste/${file.name}-${newHash}`).put(file);
      setHash(newHash);
      sessionStorage.setItem("comprovante_hash", newHash);

      File.on(
        "state_changed",
        function (snapshot) {},
        function (error) {
          enqueueSnackbar(error, { variant: "error" });
          setLoading(false);
          setSuccess(false);
        },
        function () {
          File.snapshot.ref.getDownloadURL().then(async function (downloadURL) {
            // onSuccess(null, downloadURL);

            await api
              .post("postfile", { name: file.name, downloadURL })
              .then((response) => {
                setIdFile(response.data);
                sessionStorage.setItem("comprovante_id", response.data);
                sessionStorage.setItem("file_name", file.name);
                setLoading(false);
                setFile({
                  ...file_temp,
                  name: file.name,
                  size: file.size,
                  url: downloadURL,
                });
              });
          });
        }
      );
    }

    // return isFile && isLt5M;
  };

  const handleDelFile = async (data) => {
    // debugger;
    const storageRef = await storage.ref();
    var desertRef = storageRef.child(`teste/${data.name}-${hash}`);
    // desertRef.delete();

    desertRef
      .delete()
      .then(async function () {
        await api.delete(`deletefile?id=${idFile}`);
        sessionStorage.removeItem("comprovante_id");
        sessionStorage.removeItem("comprovante_hash");
        sessionStorage.removeItem("file_name");
        setLoading(false);
        setSuccess(false);
        setIdFile(null);
        setHash(null);
        setFile({
          ...file_temp,
          name: null,
          size: null,
          url: null,
        });
      })
      .catch(function (error) {
        // Uh-oh, an error occurred!
        enqueueSnackbar("Não conseguimos atender ao seu pedido.", { variant: "error" });
        sessionStorage.removeItem("comprovante_id");
        sessionStorage.removeItem("comprovante_hash");
        sessionStorage.removeItem("file_name");
        setIdFile(null);
        setHash(null);
        setFile({
          ...file_temp,
          name: null,
          size: null,
          url: null,
        });
      });
  };

  return (
    <Grid container spacing={3} sx={{ my: 3 }}>
      <Grid item xs={12} sm={6}>
        <Box sx={{ position: 'relative' }}>
          <InputMask
            mask="99.999-999"
            value={numcep}
            disabled={loadingCep}
            maskChar=" "
            onBlur={handleCepBlur}
            onChange={handleCep}
          >
            {() => (
              <TextField
                color="warning"
                id="cep"
                label="CEP"
                name="cep"
                autoComplete="cep"
                error={Boolean(errorCep?.numcep)}
                helperText={errorCep?.numcep || (loadingCep ? "Consultando CEP..." : "")}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          </InputMask>
          {loadingCep && (
            <CircularProgress
              size={20}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                marginTop: '-10px',
              }}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="rua"
          name="rua"
          label="Rua"
          fullWidth
          autoComplete="address"
          color="warning"
          value={UseEndereco.rua}
          onChange={e => handleAddressField(e, 'rua')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="numero"
          name="numero"
          label="Número"
          fullWidth
          autoComplete="number"
          color="warning"
          value={numero}
          onChange={handleNumero}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="bairro"
          name="bairro"
          label="Bairro"
          fullWidth
          autoComplete="neighborhood"
          color="warning"
          value={UseEndereco.bairro}
          onChange={e => handleAddressField(e, 'bairro')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="municipio"
          name="municipio"
          label="Município"
          fullWidth
          autoComplete="city"
          color="warning"
          value={UseEndereco.municipio}
          onChange={e => handleAddressField(e, 'municipio')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="estado"
          name="estado"
          label="Estado"
          fullWidth
          autoComplete="state"
          color="warning"
          value={UseEndereco.estado}
          onChange={e => handleAddressField(e, 'estado')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      {/* <Grid item xs={12} sm={6}>
        <Box>
          <label htmlFor="contained-button-file">
            <Input
              accept="pdf"
              id="contained-button-file"
              multiple
              type="file"
              onChange={handleFile}
              fullWidth
            />
            <Button
              variant="contained"
              component="span"
              color="warning"
              disabled={success}
              startIcon={<FileUploadOutlinedIcon />}
            >
              Comprovante de Residência
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  color: green[500],
                  position: "absolute",
                  top: "50%",
                  left: "75%",
                  marginTop: "12px",
                  marginLeft: "12px",
                }}
              />
            )}
          </label>
          <Link href={idFile}>{file_temp.name}</Link>
          <IconButton
            color="warning"
            onClick={() => handleDelFile(file_temp)}
            aria-label="Apagar comprovante"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Grid> */}
    </Grid>
  );
}